import type { ClassValue } from 'clsx';
import type { ElementType, FC, JSX, ReactNode } from 'react';
import type { LiteralUnion } from '../../types/index.d';

import { isNumber } from '@enonic/js-utils/value/isNumber';
import { isStringLiteral } from '@enonic/js-utils/value/isStringLiteral';
import { toStr } from '@enonic/js-utils/value/toStr';
import cx from 'clsx';
import React, { type MutableRefObject } from 'react';
// import { sanitizeHtml } from '../shared/sanitizeHtml';
import {
	BgColor,
	Color,
} from '../tailwind/color';
import {
	type BorderColor,
	type BorderRadius,
	type BorderStyle,
	type BorderWidth,
	BORDER_COLOR,
	BORDER_RADIUS,
	BORDER_STYLE,
	BORDER_WIDTH,
} from '../tailwind/border';
import {
	type Display,
	type DisplayTablet,
	DISPLAY,
	DISPLAY_TABLET,
} from '../tailwind/display';
import { BoxShadow, Opacity } from '../tailwind/effects';
import { LineHeight, TextSize, Weight } from '../tailwind/font';
import {
	type FlexBasis,
	type FlexDirection,
	type FlexDirectionTablet,
	type FlexGrow,
	type FlexShrink,
	type FlexWrap,
	FLEX_BASIS,
	FLEX_SHRINK,
	FLEX_DIRECTION,
	FLEX_DIRECTION_TABLET,
	FLEX_GROW,
	FLEX_WRAP,
} from '../tailwind/flex';
import {
	type GridCols,
	// type GridColsTablet,
	// type GridColsDesktop,
	GRID_COLS,
	// GRID_COLS_TABLET,
	// GRID_COLS_DESKTOP,
} from '../tailwind/grid';
import {
	type Gap,
	type GapTablet,
	type GapDesktop,
	GAP,
	GAP_TABLET,
	GAP_DESKTOP,
} from '../tailwind/gap';
import { Height } from '../tailwind/height';
import {
	type MarginBlock,
	type MarginBlockDesktop,
	type MarginBlockTablet,
	type MarginBottom,
	type MarginInline,
	type MarginInlineDesktop,
	type MarginInlineTablet,
	type MarginRight,
	type MarginTop,
	MARGIN_BOTTOM,
	MARGIN_RIGHT,
	MARGIN_TOP,
	MARGIN_INLINE,
	MARGIN_INLINE_TABLET,
	MARGIN_INLINE_DESKTOP,
	MARGIN_BLOCK,
	MARGIN_BLOCK_TABLET,
	MARGIN_BLOCK_DESKTOP,
} from '../tailwind/margin';
import {
	type PaddingProp,
	type PaddingTabletProp,
	type PaddingDesktopProp,
	PADDING,
	PADDING_TABLET,
	PADDING_DESKTOP,
} from '../tailwind/padding';
import { Translate } from '../tailwind/transforms';
import {
	type Width,
	type WidthTablet,
	MaxWidth,
	WIDTH,
	WIDTH_TABLET,
} from '../tailwind/width';
import { isSet } from '../util/isSet';

interface DangerouslySetInnerHTML {
	__html: string;
}


const TABLET = {
	display: DISPLAY_TABLET,
	flexDirection: FLEX_DIRECTION_TABLET,
	gap: GAP_TABLET,
	marginX: MARGIN_INLINE_TABLET,
	marginY: MARGIN_BLOCK_TABLET,
	padding: PADDING_TABLET,
	width: WIDTH_TABLET,
} as const;

interface Tablet {
	display?: DisplayTablet;
	flexDirection?: FlexDirectionTablet;
	gap?: GapTablet;
	marginX?: MarginInlineTablet;
	marginY?: MarginBlockTablet
	padding?: PaddingTabletProp;
	width?: WidthTablet;
}


const DESKTOP = {
	gap: GAP_DESKTOP,
	marginX: MARGIN_INLINE_DESKTOP,
	marginY: MARGIN_BLOCK_DESKTOP,
	padding: PADDING_DESKTOP,
} as const;

interface Desktop {
	gap?: GapDesktop;
	marginX?: MarginInlineDesktop;
	marginY?: MarginBlockDesktop;
	padding?: PaddingDesktopProp;
}

export interface ClassNamesParams {
	bgColor?: keyof typeof BgColor;
	borderColor?: BorderColor;
	borderRadius?: BorderRadius;
	borderStyle?: BorderStyle;
	borderWidth?: BorderWidth;
	boxShadow?: keyof typeof BoxShadow;
	color?: keyof typeof Color;
	display?: Display;
	flexBasis?: FlexBasis;
	flexDirection?: FlexDirection;
	flexGrow?: FlexGrow;
	flexShrink?: FlexShrink;
	flexWrap?: FlexWrap;
	gap?: Gap;
	gridCols?: GridCols;
	height?: keyof typeof Height;
	lineHeight?: keyof typeof LineHeight;
	marginBottom?: MarginBottom;
	marginRight?: MarginRight;
	marginTop?: MarginTop;
	marginX?: MarginInline;
	marginY?: MarginBlock;
	maxWidth?: keyof typeof MaxWidth;
	opacity?: keyof typeof Opacity;
	padding?: PaddingProp;
	textSize?: keyof typeof TextSize;
	translate?: keyof typeof Translate;
	weight?: keyof typeof Weight;
	width?: Width;

	tablet?: Tablet;
	desktop?: Desktop;
	className?: ClassValue;
}

export type ElementProps<ELEMENT = HTMLElement> = Omit<
	React.HTMLAttributes<ELEMENT>, 'className'
> & ClassNamesParams & {
	as?: LiteralUnion<keyof JSX.IntrinsicElements>;
	elementRef?: MutableRefObject<ELEMENT | undefined>
	forceRender?: boolean;
};

type ImgProps = Omit<
	React.ImgHTMLAttributes<HTMLImageElement>, 'as'|'children'|'className'
> & ClassNamesParams;


const CLASS_TABLE = {
	bgColor: BgColor,
	borderColor: BORDER_COLOR,
	borderRadius: BORDER_RADIUS,
	borderStyle: BORDER_STYLE,
	borderWidth: BORDER_WIDTH,
	boxShadow: BoxShadow,
	color: Color,
	desktop: DESKTOP,
	display: DISPLAY,
	flexBasis: FLEX_BASIS,
	flexDirection: FLEX_DIRECTION,
	flexGrow: FLEX_GROW,
	flexShrink: FLEX_SHRINK,
	flexWrap: FLEX_WRAP,
	gap: GAP,
	gridCols: GRID_COLS,
	height: Height,
	lineHeight: LineHeight,
	marginBottom: MARGIN_BOTTOM,
	marginRight: MARGIN_RIGHT,
	marginTop: MARGIN_TOP,
	marginX: MARGIN_INLINE,
	marginY: MARGIN_BLOCK,
	maxWidth: MaxWidth,
	opacity: Opacity,
	padding: PADDING,
	tablet: TABLET,
	textSize: TextSize,
	translate: Translate,
	weight: Weight,
	width: WIDTH,
} as const;

type ClassObjKey = string | number;
type ClassObjValue = string;
interface ClassObj {
	[property: ClassObjKey]: ClassObjValue | ClassObj
}

// Recursively resolve class names from CLASS_TABLE
const resolveClassNames = (
	classObj: ClassObjValue | ClassObj,
	propValue: ClassObjKey | ClassObj | null,
	propPath: string,
	classNames: string[],
): void => {
	if (!isSet(propValue) || !classObj) return;

	// If propValue is a string or a number, try to resolve it directly
	if (isStringLiteral(propValue) || isNumber(propValue)) {
		const classStr = (classObj as ClassObj)[propValue];
		if (isStringLiteral(classStr)) {
			classNames.push(classStr);
		} else {
			console.error(`resolveClassNames: Unsupported value:${toStr(propValue)} for prop:${propPath}`);
		}
	} else if (typeof propValue === 'object' && propValue !== null) {
		// Handle nested object
		Object.keys(propValue).forEach((nestedProp) => {
			const nestedValue = propValue[nestedProp];
			const nestedClassObj = (classObj as ClassObj)[nestedProp];
			if (nestedClassObj) {
				// Recursively resolve nested class names
				resolveClassNames(nestedClassObj, nestedValue, `${propPath}.${nestedProp}`, classNames);
			} else {
				console.error(`resolveClassNames: No class definition for nested prop:${propPath}.${nestedProp}`);
			}
		});
	} else {
		console.error(`resolveClassNames: Invalid prop value:${toStr(propValue)} for prop:${propPath}`);
	}
};

export const cn = (props: ClassNamesParams): string => {
	let className: ClassValue;
	const classNames: string[] = [];

	Object.entries(props).forEach(([propName, propValue]) => {
		if (!isSet(propValue)) return; // Skip null/undefined

		const classObj = (CLASS_TABLE as ClassObj)[propName] as string | ClassObj | undefined;
		if (classObj) {
			// Resolve class names for this prop
			resolveClassNames(classObj, propValue, propName, classNames);
		} else {
			if (propName === 'className') {
				className = propValue as ClassValue;
			} else {
				console.error(`cn: Unsupported key:${propName}`);
			}
		}
	});

	// console.debug('cn classNames', classNames, 'className', className);

	return cx(classNames, className);
};

const SELF_CLOSING_TAGS = ['img', 'input', 'br', 'hr', 'meta', 'link'];

const SPECIAL_PROPS = {
	AS: 'as',
	CHILDREN: 'children',
	CLASS_NAME: 'className',
	ELEMENT_REF: 'elementRef',
	FORCE_RENDER: 'forceRender',
	DANGEROUSLY_SET_INNER_HTML :'dangerouslySetInnerHTML',
} as const;


export const Element: FC<ElementProps> = (props) => {
	let as: ElementType | undefined;
	let classNameProp: ClassValue;
	let children: ReactNode;
	let dangerouslySetInnerHTML: DangerouslySetInnerHTML | undefined;
	let elementRef: MutableRefObject<unknown> | undefined;
	let forceRender: boolean = false;

	const classNames: string[] = [];
	const otherProps: Record<string, unknown> = {};

	// Process all props
	Object.entries(props).forEach(([propName, propValue]) => {
		if (!isSet(propValue)) return; // Skip null/undefined

		const classObj = (CLASS_TABLE as ClassObj)[propName];
		if (classObj) {
			// Resolve class names for this prop
			resolveClassNames(classObj, propValue, propName, classNames);
		} else {
			// Handle special props or pass-through props
			switch (propName) {
				case SPECIAL_PROPS.CHILDREN:
					children = propValue as ReactNode;
					break;
				case SPECIAL_PROPS.DANGEROUSLY_SET_INNER_HTML:
					dangerouslySetInnerHTML = propValue as DangerouslySetInnerHTML;
					break;
				case SPECIAL_PROPS.AS:
					as = propValue as ElementType;
					break;
				case SPECIAL_PROPS.CLASS_NAME:
					classNameProp = propValue as ClassValue;
					break;
				case SPECIAL_PROPS.ELEMENT_REF:
					elementRef = propValue as React.MutableRefObject<unknown>;
					break;
				case SPECIAL_PROPS.FORCE_RENDER:
					forceRender = propValue as boolean;
					break;
				default:
					otherProps[propName] = propValue;
			}
		}
	});

	// console.debug('Element as', as, 'className', classNameProp, 'classNames', classNames, 'otherProps', otherProps);

	if (!(children || dangerouslySetInnerHTML || (as && SELF_CLOSING_TAGS.includes(as as string)) || forceRender)) {
		return null;
	}

	const Tag = (as || 'div');

	return (
		<Tag
			{...otherProps}
			className={cx(classNames, classNameProp)}
			dangerouslySetInnerHTML={dangerouslySetInnerHTML}
			ref={elementRef}
		>
			{children}
		</Tag>
	);
};

// export function Html({ html }: { html: string }): JSX.Element {
// 	return (
// 		// Using dangerouslySetInnerHTML avoids encoding < to &lt;
// 		<div dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}/>
// 	);
// }

// export const HtmlComment = ({ comment }: { comment: string }): JSX.Element => Html({ html: `<!--${comment}-->` });

export const Div: FC<Omit<ElementProps,'as'>> = ({
	// @ts-expect-error as Omitted in the type, but one might still pass it...
	as: _overridden1, // Make sure as doesn't end up in otherProps.
	...otherProps
}) => <Element {...otherProps} as='div'/>;

export type AsProps = Omit<ElementProps,'as'>;

export const Figure: FC<AsProps> = (props) => <Element {...props} as='figure' />;

export const Img: FC<ImgProps> = ({
	...otherProps
}) => otherProps.src ? <Element {...otherProps} as='img' /> : null;


type LabelProps = AsProps & {
	htmlFor?: string;
};

export const Label: FC<LabelProps> = ({
	// @ts-expect-error as Omitted in the type, but one might still pass it...
	as: _overridden1, // Make sure as doesn't end up in otherProps.
	...otherProps
}) => <Element {...otherProps} as='label'/>;

export const Legend: FC<AsProps> = ({
	...otherProps
}) => <Element {...otherProps} as='legend'/>;

export const Span: FC<AsProps> = ({
	// @ts-expect-error as Omitted in the type, but one might still pass it...
	as: _overridden1, // Make sure as doesn't end up in otherProps.
	...otherProps
}) => <Element {...otherProps} as='span'/>;


type CompatibleAnchorProps = Omit<
	// React.HTMLProps<HTMLAnchorElement>,
	React.AnchorHTMLAttributes<HTMLAnchorElement>,
	'className'
	| 'color'
	| 'translate'
> & Pick<AsProps, 'className'>;
export const Anchor: FC<CompatibleAnchorProps> = (props) => <Element {...props} as='a'/>;

type CompatibleButtonProps = Omit<
	// React.HTMLProps<HTMLButtonElement>,
	React.AnchorHTMLAttributes<HTMLButtonElement>,
	'className'
	| 'color'
	| 'translate'
> & Pick<AsProps, 'className'>;
export const Button: FC<CompatibleButtonProps> = (props) => <Element {...props} as='button'/>;

// type CompatibleInputProps = Omit<
// 	// React.HTMLProps<HTMLInputElement>,
// 	React.InputHTMLAttributes<HTMLInputElement>,
// 	'className'
// 	| 'color'
// 	| 'height'
// 	| 'translate'
// 	| 'width'
// > & Omit<ElementProps<HTMLInputElement>, 'as'>; // Pick<AsProps, 'className'>;
// export const Input: FC<CompatibleInputProps> = (props) => <Element {...props} as='input'/>;
